import { useCallback, useMemo } from 'react';

import { useRouter } from 'expo-router';
import { useStoreContext } from 'features/store';

import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import useFoodware from 'hooks/use-foodware';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import { makeOrderReorderable } from 'hooks/use-reorderable-orders/utils';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { EnableMenuReorderingAvailabilityOverrideVariation } from 'state/launchdarkly/variations';
import { LoyaltyReward } from 'state/loyalty/types';
import { useOrderContext } from 'state/order';
import { IPendingReorder } from 'state/order/types';
import { useServiceModeContext } from 'state/service-mode';
import { routes } from 'utils/routing';

import { isOfferType } from '../utils';

export const useReorder = (order?: IPendingReorder | null, url = routes.orders) => {
  const {
    reorder: { handleReorder, setPendingReorder, setShouldHandleReorder },
  } = useOrderContext();
  const { isStoreOpenAndAvailable, noStoreSelected } = useStoreContext();
  const { isDelivery } = useServiceModeContext();
  const enableReorder = useFlag(LaunchDarklyFlag.ENABLE_REORDER);
  const router = useRouter();

  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { allRewards } = useLoyaltyRewardsList();

  const allRewardsMap = useMemo(() => {
    const dictionary: Record<string, LoyaltyReward> = {};
    (allRewards ?? []).forEach(item => (dictionary[item._id] = item));
    return dictionary;
  }, [allRewards]);

  // Temporary flag to disable reordering of items/combos based on Sanity IDs. Should be removed once One Menu is live.
  const sanityIdsExcludedFromReordering = useFlag<
    EnableMenuReorderingAvailabilityOverrideVariation
  >(LaunchDarklyFlag.ENABLE_MENU_REORDERING_AVAILABILITY_OVERRIDE);

  const { isFoodware } = useFoodware();

  const isOrderExcludedFromReordering = useMemo(() => {
    if (!order?.cart.cartEntries.length) {
      return true;
    }

    if (order.cart.cartEntries.length === 1) {
      return (sanityIdsExcludedFromReordering ?? []).includes(order.cart.cartEntries[0].sanityId);
    }

    return order.cart.cartEntries.some(entry => isOfferType(entry.type));
  }, [order, sanityIdsExcludedFromReordering]);

  const handleReorderClick = useCallback(() => {
    if (!order) {
      return;
    }

    const reorderableOrder = order.cart.reorderCartEntries
      ? order
      : makeOrderReorderable({
          order,
          sanityIdsExcludedFromReordering,
          isFoodware,
          allRewardsMap,
          isUnifiedMenuEnabled,
        });

    if (isStoreOpenAndAvailable) {
      handleReorder(reorderableOrder, url);
    } else {
      setPendingReorder(reorderableOrder);
      setShouldHandleReorder(true);
      router.replace(isDelivery ? routes.deliveryAddress : routes.storeLocator);
    }
  }, [
    order,
    sanityIdsExcludedFromReordering,
    isFoodware,
    allRewardsMap,
    isUnifiedMenuEnabled,
    isStoreOpenAndAvailable,
    handleReorder,
    url,
    setPendingReorder,
    setShouldHandleReorder,
    router,
    isDelivery,
  ]);

  const isReorderingDisabled = useMemo(() => {
    const isSelectedStoreNotOpenAndAvailable = !isStoreOpenAndAvailable && !noStoreSelected;

    return !enableReorder || isSelectedStoreNotOpenAndAvailable || isOrderExcludedFromReordering;
  }, [enableReorder, isStoreOpenAndAvailable, noStoreSelected, isOrderExcludedFromReordering]);

  return { handleReorderClick, isReorderingDisabled };
};
